import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCanAccessRoute } from '../../hooks/usePermissions';
import { ProtectedRouteProps } from '../../types/auth';
import LoadingSpinner from '../common/LoadingSpinner';

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  fallback,
  permissions,
  roles,
  requireAuth = true,
  redirectTo = '/login',
}) => {
  // console.log('🔍 ProtectedRoute called with:', { permissions, roles, requireAuth });
  
  const { isAuthenticated, isInitialized, isLoading } = useAuth();
  const canAccess = useCanAccessRoute(permissions, roles, requireAuth);
  const location = useLocation();

  // Aguardar inicialização da autenticação
  if (!isInitialized || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Se não requer autenticação, renderizar normalmente
  if (!requireAuth) {
    return <>{children}</>;
  }

  // Se não está autenticado, redirecionar para login
  if (!isAuthenticated) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Se não tem permissão, mostrar acesso negado
  if (!canAccess) {
    if (fallback) {
      return <>{fallback}</>;
    }
    
    return (
      <Navigate
        to="/access-denied"
        state={{ 
          from: location.pathname,
          requiredPermissions: permissions,
          requiredRoles: roles 
        }}
        replace
      />
    );
  }

  // Usuário autenticado e com permissões
  return <>{children}</>;
};

// HOC para facilitar uso
export const withProtectedRoute = (
  Component: React.ComponentType,
  options: Omit<ProtectedRouteProps, 'children'> = {}
) => {
  return (props: any) => (
    <ProtectedRoute {...options}>
      <Component {...props} />
    </ProtectedRoute>
  );
};

// Hook para usar proteção em componentes
export const useProtectedComponent = (
  permissions?: string[],
  roles?: string[]
) => {
  const { isAuthenticated } = useAuth();
  const canAccess = useCanAccessRoute(permissions, roles, true);
  
  return {
    isAuthenticated,
    canAccess,
    shouldRender: isAuthenticated && canAccess,
  };
};

export default ProtectedRoute;